"use client";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  HeaderGroup,
} from "@tanstack/react-table";
import classNames from "classnames";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/ui/components/ui/table.tsx";
import Pagination from "@/shared/components/ui/table/pagination.tsx";
import EmptyState from "@/shared/components/ui/empty-state/empty-state.tsx";

export type ColumnDefCustom<T> = ColumnDef<T> & {
  fixed?: boolean;
  groupKey?: string;
  groupRender?: () => React.ReactNode;
};

export interface TemplateTableProps<T> {
  items?: T[];
  columns: ColumnDefCustom<T>[];
  pagination?: any;
  filterKey: string;
  isLoading: boolean;
  onRowClick?: (row: T) => void;
}

const TemplateTable = <T,>({
                             items,
                             pagination,
                             filterKey,
                             isLoading,
                             columns,
                             onRowClick,
                           }: TemplateTableProps<T>) => {
  const table = useReactTable({
    data: items ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const FIXED_COLUMN_WIDTH = 0;

  return (
    <div className="w-full">
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="w-full">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup: HeaderGroup<T>) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent border-b border-border">
                  {headerGroup.headers.map((header, index) => {
                    const columnDef = header.column.columnDef as ColumnDefCustom<T>;
                    const groupKey = columnDef?.groupKey || header.id;
                    const sameGroup =
                      index > 0 &&
                      (headerGroup.headers[index - 1].column.columnDef as ColumnDefCustom<T>)?.groupKey === groupKey;

                    return sameGroup ? null : (
                      <TableHead
                        key={header.id}
                        colSpan={
                          headerGroup.headers.filter(
                            (h) => (h.column.columnDef as ColumnDefCustom<T>).groupKey === groupKey
                          ).length
                        }
                        className="bg-muted/50 font-semibold text-foreground h-12 px-4"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index} className="animate-pulse hover:bg-transparent">
                    {columns.map((col, colIndex) => (
                      <TableCell key={colIndex} className="h-16 px-4 whitespace-nowrap">
                        <div className="h-4 bg-muted/50 rounded w-full max-w-[200px]"></div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))
                : table.getRowModel().rows.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={columns.length} className="text-center py-12">
                      <EmptyState className="min-h-[200px]" />
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      className={classNames(
                        "transition-colors",
                        onRowClick && "cursor-pointer hover:bg-muted/50"
                      )}
                      onClick={() => onRowClick && onRowClick(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell
                          key={cell.id}
                          className="h-16 px-4 whitespace-nowrap"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Pagination pagination={pagination} filterKey={filterKey} />
    </div>
  );
};

export default TemplateTable;
